import '@fontsource-variable/noto-sans';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { ErrorBoundary, Provider as RollbarProvider } from '@rollbar/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import { DateLocalizationProvider } from './components/DateLocalizationProvider';
import { ErrorFallback } from './components/ErrorFallback';
import { initI18n } from './i18n';
import { currentUserQuery, selectRollbarPerson } from './lib/auth';
import { HouzyDevtools } from './lib/devtools/HouzyDevtools';
import { MixpanelProvider } from './lib/mixpanel';
import { isMixpanelSuppressedFor, MixpanelIdentity } from './lib/mixpanelIdentity';
import { setMixpanelSuppressionResolver } from './lib/mixpanelSuppression';
// newrelicBrowser must be imported before the React tree so the agent starts
// capturing page load timing
import './lib/newrelic';
import { queryClient } from './lib/queryClient';
import { rollbar, setRollbarPersonResolver } from './lib/rollbar';
import { router } from './router';
import { theme } from './theme';

const rootElement = document.getElementById('root');
if (!rootElement) {
    throw new Error('Missing root element (#root)');
}

void initI18n();

setRollbarPersonResolver(() => selectRollbarPerson(queryClient.getQueryData(currentUserQuery.queryKey)));
setMixpanelSuppressionResolver(() => isMixpanelSuppressedFor(queryClient.getQueryData(currentUserQuery.queryKey)));

createRoot(rootElement).render(
    <StrictMode>
        <RollbarProvider instance={rollbar}>
            <ThemeProvider theme={theme} defaultMode="light">
                <CssBaseline />
                <ErrorBoundary fallbackUI={ErrorFallback}>
                    <MixpanelProvider>
                        <QueryClientProvider client={queryClient}>
                            <MixpanelIdentity />
                            {/* TODO: Possibly add a loading state */}
                            <Suspense fallback={null}>
                                <DateLocalizationProvider>
                                    <RouterProvider router={router} />
                                    {/* Use `import.meta.env.DEV` directly (not `config.isDevMode`) so Vite strips the dev-only devtools
                                        from production bundles. */}
                                    {import.meta.env.DEV && <HouzyDevtools />}
                                </DateLocalizationProvider>
                            </Suspense>
                        </QueryClientProvider>
                    </MixpanelProvider>
                </ErrorBoundary>
            </ThemeProvider>
        </RollbarProvider>
    </StrictMode>,
);
